// The RIGHT panel's Diagram mode (TODO §21/§8) — the attribute editor for whatever is
// selected on the canvas.
//
// The three mechanics that make this possible are all public API, and §8.6's spike exists to
// prove them before this file was written:
//
//   read   — `excalidrawAPI.onChange(...)` + `getAppState().selectedElementIds` +
//            `getSceneElements()`. Here the subscription is the editing context's
//            `sceneTick`, which the mounted editor bumps; the panel then asks the API for
//            what it needs rather than being fed a 60fps copy of the scene.
//   write  — `newElementWith` (non-mutating, React-safe) + `updateScene`.
//   undo   — `CaptureUpdateAction.IMMEDIATELY`, which is exactly what makes **Cmd+Z undo a
//            panel edit** the same way it undoes a canvas edit. Without it a property change
//            made here would be invisible to the history and the author's next undo would
//            skip past it to something they did minutes ago.

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  sceneVersion,
  subscribeScene,
  useDiagramEditing,
} from "@/components/deck/diagramEditing";
import {
  DIAGRAM_FRAME_NAME,
  FRAME_ORIGIN,
  fittedFrameSize,
  resolveFrame,
} from "@/lib/diagram";
import { loadDiagramEngine, type DiagramEngine } from "@/lib/diagram-engine";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/store/hooks";

// §9.5 Fonts are an ENUM, not a free field. Excalidraw's `FONT_FAMILY` is a fixed set and the
// deck stylesheet's `fontFamilies` is an arbitrary list; they do not map, and pretending they
// do produces text that is right in the editor and subtly wrong in the export. So the panel
// offers the enum with honest labels and says so.
const FONT_LABELS: {
  key: "Excalifont" | "Nunito" | "Cascadia";
  label: string;
}[] = [
  { key: "Nunito", label: "Normal" },
  { key: "Excalifont", label: "Hand-drawn" },
  { key: "Cascadia", label: "Code" },
];

const STROKE_WIDTHS = [
  { value: 1, label: "Thin" },
  { value: 2, label: "Bold" },
  { value: 4, label: "Extra bold" },
];
const STROKE_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
];
const FILL_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "hachure", label: "Hachure" },
  { value: "cross-hatch", label: "Cross-hatch" },
];
const SLOPPINESS = [
  { value: 0, label: "Architect" },
  { value: 1, label: "Artist" },
  { value: 2, label: "Cartoonist" },
];
const TEXT_ALIGN = [
  { value: "left", label: "Left" },
  { value: "center", label: "Centre" },
  { value: "right", label: "Right" },
];
const FONT_SIZES = [
  { value: 16, label: "S" },
  { value: 20, label: "M" },
  { value: 28, label: "L" },
  { value: 36, label: "XL" },
];

// A minimal structural read of a scene element — the same narrowing `diagram.ts` does, for
// the same reason: the element shape is Excalidraw's to define.
type El = Record<string, unknown> & { id: string; type: string };

// The shared value of one property across the selection, or `undefined` when they disagree
// (§8.2's "mixed indicator").
function shared<T>(elements: El[], key: string): T | undefined {
  if (elements.length === 0) return undefined;
  const first = elements[0][key] as T;
  return elements.every((el) => el[key] === first) ? first : undefined;
}

export function DiagramPanel() {
  const { api, editingDocId, getWindowSize } = useDiagramEditing();
  // §8.2 The subscription. An external store rather than context state: `onChange` fires on
  // pointer moves and selection changes, and routing that through the context re-rendered the
  // whole deck at 60fps — and looped (see the long note in `diagramEditing.tsx`). This way the
  // 60fps stream reaches exactly this component.
  const sceneTick = useSyncExternalStore(
    subscribeScene,
    sceneVersion,
    sceneVersion,
  );
  const styleSheet = useAppSelector((s) => s.document.styleSheet);
  const doc = useAppSelector((s) =>
    editingDocId ? s.diagram[editingDocId] : undefined,
  );
  const swatches = useMemo(() => sheetSwatches(styleSheet), [styleSheet]);

  // The engine, once the editor has loaded it — which by definition it has, since this panel
  // is only useful while an editor is mounted. Held in state rather than awaited per action
  // so a colour click is synchronous.
  const [engine, setEngine] = useState<DiagramEngine | null>(null);
  useEffect(() => {
    let live = true;
    void loadDiagramEngine().then((e) => {
      if (live) setEngine(e);
    });
    return () => {
      live = false;
    };
  }, []);

  // §8.2 Re-read the live selection off the API whenever the editor reports a scene change.
  // `sceneTick` is the dependency that makes this a subscription rather than a poll.
  const { selected, appState } = useMemo(() => {
    if (!api) return { selected: [] as El[], appState: null };
    const state = api.getAppState();
    const ids = state.selectedElementIds;
    return {
      selected: (api.getSceneElements() as unknown as El[]).filter(
        (el) => ids[el.id],
      ),
      appState: state,
    };
    // sceneTick is the whole point of this dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, sceneTick]);

  // §26 THE MAPPING, computed before it is applied — because "match deck colours" on its own
  // is a button that does something unpredictable to a drawing, and the honest complaint about
  // it was "what colours, to what?". Nearest-by-RGB-distance is a defensible rule and an
  // unguessable one: whether a mid-grey stroke lands on the deck's ink or its navy depends on
  // arithmetic nobody can do by eye. So the panel lists every distinct colour the drawing
  // actually uses beside the swatch it would become, and the button says how many change.
  //
  // Recomputed off `sceneTick`, so it tracks the drawing as it is edited.
  const colorPlan = useMemo(() => {
    if (!engine || swatches.length === 0) return [];
    void sceneTick;
    const seen = new Map<string, { from: string; to: string }>();
    for (const el of api?.getSceneElements() ?? []) {
      const source = el as unknown as El;
      for (const raw of [source.strokeColor, source.backgroundColor]) {
        const from = String(raw ?? "");
        // "transparent" is a choice, not a colour: snapping it would fill every unfilled
        // shape on the canvas.
        if (!from || from === "transparent" || seen.has(from)) continue;
        const to = nearestSwatch(from, swatches);
        if (to && to.toLowerCase() !== from.toLowerCase())
          seen.set(from, { from, to });
      }
    }
    return [...seen.values()];
  }, [api, engine, swatches, sceneTick]);

  if (!editingDocId || !api) {
    return (
      <div data-diagram-ui className="p-4 text-sm text-slate-500">
        Double-click a diagram on the slide to edit it. Its elements and their
        properties appear here.
      </div>
    );
  }

  // §8.3 The one write path. `newElementWith` produces a NEW element with a bumped version
  // rather than mutating in place, and `CaptureUpdateAction.IMMEDIATELY` is what lands the
  // change in the undo history as one user-initiated step.
  const patchSelection = (patch: Record<string, unknown>) => {
    if (!engine) return;
    const ids = api.getAppState().selectedElementIds;
    const elements = api.getSceneElements();
    api.updateScene({
      elements: elements.map((el) =>
        ids[el.id] ? engine.newElementWith(el, patch as never) : el,
      ),
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // Layer order. Excalidraw's own z-order actions are internal, but the scene array IS the
  // order, so moving the selection to either end of it is the whole operation — and going
  // through `updateScene` means `convertToExcalidrawElements`' fractional indices are
  // recomputed by the package rather than by us.
  const reorder = (toFront: boolean) => {
    if (!engine) return;
    const ids = api.getAppState().selectedElementIds;
    const elements = api.getSceneElements();
    const picked = elements.filter((el) => ids[el.id]);
    const rest = elements.filter((el) => !ids[el.id]);
    api.updateScene({
      elements: toFront ? [...rest, ...picked] : [...picked, ...rest],
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // Canvas-level settings live on `appState`, not on elements.
  const patchAppState = (patch: Record<string, unknown>) => {
    if (!engine) return;
    api.updateScene({
      appState: patch as never,
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // §3.4 "Fit frame to window" — the EXPLICIT, undoable version of the resize that §3.4
  // refuses to do automatically. A layout change letterboxes; this is how an author says
  // "actually, re-author the page at this shape", and Cmd+Z puts it back.
  //
  // It also CREATES the page when there is none, which is the only way back from a deleted
  // frame — hence the button stays enabled in that state rather than going dead at the exact
  // moment it is most needed.
  const fitFrameToWindow = () => {
    if (!engine) return;
    const size = getWindowSize(editingDocId);
    if (!size) return;
    const fitted = fittedFrameSize(size);
    const elements = api.getSceneElements();
    const existing = doc ? resolveFrame(doc) : null;

    if (existing) {
      api.updateScene({
        elements: elements.map((el) =>
          el.id === existing.id
            ? engine.newElementWith(el, fitted as never)
            : el,
        ),
        captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
      });
      return;
    }

    // No page: build one around what is already drawn, so restoring a frame does not also
    // move the drawing out of it. The x/y are written explicitly for the reason spelled out
    // in `createDiagramDoc` — `convertToExcalidrawElements` drops an origin of 0.
    const [minX, minY] = engine.getCommonBounds(elements);
    const x = Number.isFinite(minX) ? Math.round(minX) : FRAME_ORIGIN.x;
    const y = Number.isFinite(minY) ? Math.round(minY) : FRAME_ORIGIN.y;
    const built = engine.convertToExcalidrawElements([
      {
        type: "frame",
        children: [],
        name: DIAGRAM_FRAME_NAME,
        x,
        y,
        ...fitted,
      },
    ]);
    const frame = built.find((el) => el.type === "frame");
    if (!frame) return;
    api.updateScene({
      elements: [
        engine.newElementWith(frame, { x, y, ...fitted } as never),
        ...elements,
      ],
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // §9.3 "Match deck colors" — the honest version of the automatic restyling §9.1 refuses to
  // do. Same result, initiated by the person who can see whether it worked, and one undo
  // step. It remaps every element's stroke and background to the NEAREST named sheet colour,
  // so a drawing made against another deck's palette can be brought into this one without
  // recolouring twenty shapes by hand.
  const matchDeckColors = () => {
    if (!engine || swatches.length === 0) return;
    const elements = api.getSceneElements();
    api.updateScene({
      elements: elements.map((el) => {
        const patch: Record<string, unknown> = {};
        const stroke = nearestSwatch(
          String((el as unknown as El).strokeColor ?? ""),
          swatches,
        );
        if (stroke) patch.strokeColor = stroke;
        const bg = String((el as unknown as El).backgroundColor ?? "");
        // "transparent" is a choice, not a colour: snapping it to the nearest swatch would
        // fill every unfilled shape on the canvas.
        if (bg && bg !== "transparent") {
          const next = nearestSwatch(bg, swatches);
          if (next) patch.backgroundColor = next;
        }
        return Object.keys(patch).length
          ? engine.newElementWith(el, patch as never)
          : el;
      }),
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  const frame = doc ? (resolveFrame(doc)?.rect ?? null) : null;
  const hasSelection = selected.length > 0;
  const hasText = selected.some(
    (el) => el.type === "text" || typeof el.boundElements !== "undefined",
  );
  const hasArrow = selected.some((el) => el.type === "arrow");

  return (
    // §6.4 Part of the editing session — see `DiagramPalette`'s note. Without this, the
    // first property change would close the editor whose property it changed.
    <div
      data-diagram-ui
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4"
    >
      {/* ── Selection ──────────────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Selection{" "}
          {hasSelection && (
            <span className="font-normal normal-case text-slate-400">
              — {selected.length} element{selected.length === 1 ? "" : "s"}
            </span>
          )}
        </h3>

        {!hasSelection ? (
          <p className="text-sm text-slate-500">
            Nothing selected. Pick an element on the canvas, or drop one from
            the Elements panel on the left.
          </p>
        ) : (
          <>
            <SwatchRow
              label="Stroke"
              swatches={swatches}
              value={shared<string>(selected, "strokeColor")}
              onPick={(value) => patchSelection({ strokeColor: value })}
            />
            <SwatchRow
              label="Background"
              swatches={swatches}
              allowTransparent
              value={shared<string>(selected, "backgroundColor")}
              onPick={(value) => patchSelection({ backgroundColor: value })}
            />
            <Choice
              label="Fill"
              options={FILL_STYLES}
              value={shared<string>(selected, "fillStyle")}
              onPick={(value) => patchSelection({ fillStyle: value })}
            />
            <Choice
              label="Stroke width"
              options={STROKE_WIDTHS}
              value={shared<number>(selected, "strokeWidth")}
              onPick={(value) => patchSelection({ strokeWidth: value })}
            />
            <Choice
              label="Stroke style"
              options={STROKE_STYLES}
              value={shared<string>(selected, "strokeStyle")}
              onPick={(value) => patchSelection({ strokeStyle: value })}
            />
            <Choice
              label="Sloppiness"
              options={SLOPPINESS}
              value={shared<number>(selected, "roughness")}
              onPick={(value) => patchSelection({ roughness: value })}
            />
            <Choice
              label="Edges"
              options={[
                { value: "sharp", label: "Sharp" },
                { value: "round", label: "Round" },
              ]}
              value={
                shared<unknown>(selected, "roundness") === null
                  ? "sharp"
                  : "round"
              }
              onPick={(value) =>
                patchSelection({
                  roundness: value === "sharp" ? null : { type: 3 },
                })
              }
            />

            {hasText && (
              <>
                <Choice
                  label="Font"
                  options={FONT_LABELS.map((f) => ({
                    value: engine?.FONT_FAMILY[f.key] ?? 0,
                    label: f.label,
                  }))}
                  value={shared<number>(selected, "fontFamily")}
                  onPick={(value) => patchSelection({ fontFamily: value })}
                />
                <Choice
                  label="Font size"
                  options={FONT_SIZES}
                  value={shared<number>(selected, "fontSize")}
                  onPick={(value) => patchSelection({ fontSize: value })}
                />
                <Choice
                  label="Text align"
                  options={TEXT_ALIGN}
                  value={shared<string>(selected, "textAlign")}
                  onPick={(value) => patchSelection({ textAlign: value })}
                />
                <p className="flex gap-1.5 text-[11px] leading-snug text-slate-400">
                  <Info className="mt-px h-3 w-3 shrink-0" />
                  {/* §9.5, said out loud rather than papered over. */}
                  Diagram text uses the diagram's own fonts, not the deck
                  stylesheet's — the two lists are different by design.
                </p>
              </>
            )}

            {hasArrow && (
              <Choice
                label="Arrowheads"
                options={[
                  { value: "arrow", label: "End" },
                  { value: "triangle", label: "Triangle" },
                  { value: "dot", label: "Dot" },
                ]}
                value={shared<string>(selected, "endArrowhead")}
                onPick={(value) => patchSelection({ endArrowhead: value })}
              />
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Opacity</Label>
              <Slider
                value={[shared<number>(selected, "opacity") ?? 100]}
                min={10}
                max={100}
                step={10}
                onValueChange={([v]) => patchSelection({ opacity: v })}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Layer</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => reorder(false)}
                >
                  Send to back
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => reorder(true)}
                >
                  Bring to front
                </Button>
              </div>
              <p className="text-[11px] leading-snug text-slate-400">
                Grouping and element links stay on the canvas — Excalidraw's own
                shortcuts (⌘G, ⌘⇧G) work while it has focus.
              </p>
            </div>
          </>
        )}
      </section>

      {/* ── Diagram ────────────────────────────────────────────────────────────────── */}
      <section className="space-y-3 border-t border-slate-200 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Diagram
        </h3>

        <SwatchRow
          label="Canvas background"
          swatches={swatches}
          value={String(appState?.viewBackgroundColor ?? "")}
          onPick={(value) => patchAppState({ viewBackgroundColor: value })}
        />

        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-500">Grid</Label>
          <Switch
            checked={!!appState?.gridModeEnabled}
            onCheckedChange={(on) => patchAppState({ gridModeEnabled: on })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-500">Snap to objects</Label>
          <Switch
            checked={!!appState?.objectsSnapModeEnabled}
            onCheckedChange={(on) =>
              patchAppState({ objectsSnapModeEnabled: on })
            }
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">Page</Label>
          <p className="text-[11px] leading-snug text-slate-500">
            {frame
              ? `The slide shows this diagram's page frame — ${Math.round(frame.width)} × ${Math.round(frame.height)}. Anything outside it is scratch space and never appears on the slide or in an export.`
              : "This diagram has no page frame, so the slide shows everything you have drawn. Fit it to the window to give it one."}
          </p>
          <Button size="sm" variant="secondary" onClick={fitFrameToWindow}>
            {frame ? "Fit frame to window" : "Add a page frame"}
          </Button>
          <p className="text-[11px] leading-snug text-slate-400">
            {/* §3.4 said in the UI, because the behaviour is deliberate and otherwise reads
                as a bug: a layout change letterboxes the diagram rather than re-cropping it. */}
            Changing the slide's layout letterboxes the diagram; it never
            re-crops it. This button is the deliberate version, and ⌘Z undoes
            it.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">Deck colours</Label>
          {/* §26 The mapping, before it is applied. Each row is a colour this drawing uses and
              the deck colour it would become — the answer to "what colours, to what?", which a
              button labelled only "Match deck colours" cannot give. */}
          {colorPlan.length > 0 ? (
            <ul className="space-y-1">
              {colorPlan.map((row) => (
                <li
                  key={row.from}
                  className="flex items-center gap-1.5 text-[11px] text-slate-500"
                >
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-sm border border-slate-300"
                    style={{ backgroundColor: row.from }}
                  />
                  <span className="font-mono">{row.from}</span>
                  <span aria-hidden>→</span>
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-sm border border-slate-300"
                    style={{ backgroundColor: row.to }}
                  />
                  <span className="font-mono">
                    {swatches.find((c) => c.value === row.to)?.name ?? row.to}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] leading-snug text-slate-400">
              Every colour in this drawing is already one of the deck&apos;s.
            </p>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={matchDeckColors}
            disabled={colorPlan.length === 0}
          >
            {colorPlan.length === 0
              ? "Match deck colours"
              : `Match ${colorPlan.length} colour${colorPlan.length === 1 ? "" : "s"}`}
          </Button>
          <p className="text-[11px] leading-snug text-slate-400">
            {/* §9.1's divergence, stated where it will be noticed. */}A diagram
            is never restyled automatically when the deck&apos;s stylesheet
            changes — &quot;which of five colours was the accent?&quot; has no
            right answer, and guessing wrong ruins a drawing. This does it on
            request, matching each colour to the nearest deck colour, as one
            undo step.
          </p>
        </div>
      </section>
    </div>
  );
}

// A row of the deck's own named colours (§9.2) plus a custom field. The sheet drives the
// defaults OFFERED; it never rewrites what is already on the canvas (§9.1).
function SwatchRow({
  label,
  swatches,
  value,
  onPick,
  allowTransparent = false,
}: {
  label: string;
  swatches: { name: string; value: string }[];
  value: string | undefined;
  onPick: (value: string) => void;
  allowTransparent?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">
        {label}
        {value === undefined && (
          <span className="ml-1 text-slate-400">(mixed)</span>
        )}
      </Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {allowTransparent && (
          <button
            type="button"
            onClick={() => onPick("transparent")}
            title="Transparent"
            aria-label="Transparent"
            className={cn(
              "h-6 w-6 rounded border",
              value === "transparent"
                ? "border-slate-900 ring-2 ring-slate-900/20"
                : "border-slate-300",
            )}
            style={{
              background:
                "repeating-conic-gradient(#e2e8f0 0% 25%, #fff 0% 50%) 50%/8px 8px",
            }}
          />
        )}
        {swatches.map((c) => (
          <button
            key={`${c.name}:${c.value}`}
            type="button"
            onClick={() => onPick(c.value)}
            title={c.name}
            aria-label={c.name}
            className={cn(
              "h-6 w-6 rounded border",
              value?.toLowerCase() === c.value.toLowerCase()
                ? "border-slate-900 ring-2 ring-slate-900/20"
                : "border-slate-300",
            )}
            style={{ background: c.value }}
          />
        ))}
        <input
          type="color"
          value={normalizeHex(value)}
          onChange={(e) => onPick(e.target.value)}
          aria-label={`${label} — custom colour`}
          title="Custom colour"
          className="h-6 w-8 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
        />
      </div>
    </div>
  );
}

// A labelled row of mutually exclusive buttons. Small enough to be worth having in one place
// rather than as six near-identical blocks.
function Choice<T extends string | number>({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | undefined;
  onPick: (value: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">
        {label}
        {value === undefined && (
          <span className="ml-1 text-slate-400">(mixed)</span>
        )}
      </Label>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onPick(o.value)}
            className={cn(
              "rounded border px-2 py-1 text-xs",
              value === o.value
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-400",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// `<input type="color">` refuses anything but `#rrggbb`, and an element's colour can be
// "transparent", a 3-digit hex or a named colour. Fall back to black rather than letting the
// input reset itself to a value nobody chose.
function normalizeHex(value: string | undefined): string {
  if (!value) return "#000000";
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!m) return "#000000";
  const h = m[1];
  return h.length === 3
    ? `#${h
        .split("")
        .map((c) => c + c)
        .join("")}`
    : `#${h}`;
}

// === §9.3's "nearest" ===
//
// Nearest in sRGB by squared distance. Not perceptually uniform — CIELAB would be — but the
// input is a handful of deck colours and an author who can see the result, and a wrong answer
// here is one Cmd+Z rather than a silent corruption. Unparseable colours are left alone.
function nearestSwatch(
  color: string,
  swatches: { name: string; value: string }[],
): string | null {
  const target = parseHex(color);
  if (!target) return null;
  let best: { value: string; d: number } | null = null;
  for (const c of swatches) {
    const rgb = parseHex(c.value);
    if (!rgb) continue;
    const d =
      (rgb[0] - target[0]) ** 2 +
      (rgb[1] - target[1]) ** 2 +
      (rgb[2] - target[2]) ** 2;
    if (!best || d < best.d) best = { value: c.value, d };
  }
  return best?.value ?? null;
}

function parseHex(value: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!m) return null;
  const h =
    m[1].length === 3
      ? m[1]
          .split("")
          .map((c) => c + c)
          .join("")
      : m[1];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}
